import React, { useRef, useCallback } from 'react';
import Button from '@mui/material/Button';
import ReactToPrint from 'react-to-print';
import InvoiceReport from './InvoiceReport';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment-timezone';
import { hasPermission } from 'app/main/loadz/utils';
import { useTranslation } from 'react-i18next';
import { exportSalesReportCsv, getReportTypeTitleKey } from './salesReportUtils';

export default function InvoicePrintReport({
  company_id,
  groupByCustomer,
  groupByJob,
  reportType,
  compLogo,
  show,
  fullReportData,
  display,
  date_start,
  date_end,
  selectedCustomers = [],
  selectedJobs = [],
  selectedStatus = [],
}) {
  const { t } = useTranslation('navigation');
  let start_date = date_start;
  let end_date = date_end;
  let enddate = date_end;
  let has_date = true;

  if (start_date && end_date) {
    if (moment(start_date).isAfter(end_date)) {
      const temp = end_date;
      end_date = start_date;
      start_date = temp;
    }
  } else if (start_date || enddate) {
    if (start_date && !enddate) {
      end_date = start_date;
    } else if (!start_date && enddate) {
      start_date = end_date;
    }
  } else {
    has_date = false;
  }

  if (has_date) {
    start_date = moment(start_date).format('YYYY-MM-DD');
    end_date = moment(end_date).format('YYYY-MM-DD');
  }

  let customer_list = [];
  if (selectedCustomers.find((agent) => agent.customer_id == 'all')) {
    customer_list = ['all'];
  } else {
    customer_list = selectedCustomers.map((i) => i.name);
  }

  let job_list = [];
  if (selectedJobs.find((job) => job.job_id == 'all')) {
    job_list = ['all'];
  } else {
    job_list = selectedJobs.map((i) => i.job_no);
  }

  let status_list = [];
  if (selectedStatus.find((stat) => stat.id == 'all')) {
    status_list = ['all'];
  } else {
    status_list = selectedStatus.map((i) => i.status);
  }

  const componentRef = useRef();

  const printTrigger = useCallback(() => {
    return show ? (
      <IconButton style={{ fontSize: 14 }}>
        {show}&nbsp;
        <PrintIcon />
      </IconButton>
    ) : (
      <Tooltip title={t('PRINT')}>
        <IconButton>
          <PrintIcon />
        </IconButton>
      </Tooltip>
    );
  }, [show, t]);

  const handleExport = () => {
    exportSalesReportCsv({
      reportData: fullReportData,
      reportType,
      groupByCustomer,
      groupByJob,
      companyId: company_id,
      showCommission: hasPermission('invoice.read'),
      startDate: has_date ? start_date : '',
      endDate: has_date ? end_date : '',
      t,
    });
  };

  const documentTitle = `${t(getReportTypeTitleKey(reportType)).replace(/\s+/g, '_')}_STATUS_${status_list.join(
    '_'
  )}_CUSTOMERS_${customer_list.join('_')}_JOBS_${job_list.join('_')}_DATE_${
    has_date ? start_date + '_to_' + end_date : 'all'
  }`;

  const reportProps = {
    company_id,
    usergroupByJob: groupByJob,
    usergroupByCustomer: groupByCustomer,
    reportType,
    compLogo,
    fullReportData,
    has_date,
    start_date,
    end_date,
  };

  return (
    <div className='w-full'>
      <div className='flex flex-row items-center justify-center gap-8 pb-8'>
        <ReactToPrint trigger={printTrigger} documentTitle={documentTitle} content={() => componentRef.current} />
        <Tooltip title={t('EXPORT_CSV')}>
          <Button
            variant='contained'
            size='small'
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            sx={{ backgroundColor: '#edca33', color: '#1f1f1f', '&:hover': { backgroundColor: '#d4b42c' } }}
          >
            {t('EXPORT')}
          </Button>
        </Tooltip>
      </div>

      {display ? (
        <div className='w-full overflow-x-auto'>
          <InvoiceReport ref={componentRef} {...reportProps} />
        </div>
      ) : (
        <div style={{ display: 'none' }}>
          <InvoiceReport ref={componentRef} {...reportProps} />
        </div>
      )}
    </div>
  );
}
